<template>
  <div :class="wrapperClass">
    <label
      v-if="label" :class="[
        {
          'text-xs uppercase': uppercaseLabels,
          'text-sm': !uppercaseLabels,
        },
      ]"
    >
      {{ label }}
      <span v-if="required" class="required-dot text-moone-amber">*</span>
    </label>
    <small v-if="help && helpPosition=='above_input'" :class="theme.default.help" class="flex mb-1">
      <slot name="help"><span class="field-help" v-html="help" /></slot>
    </small>

    <div class="flex w-full items-center justify-center transition-colors duration-40"
         :class="[{'!cursor-not-allowed':disabled, 'cursor-pointer':!disabled,
                   [theme.fileInput.inputHover.light + ' dark:'+theme.fileInput.inputHover.dark]: uploadDragoverEvent,
                   ['hover:'+theme.fileInput.inputHover.light +' dark:hover:'+theme.fileInput.inputHover.dark]: !loading}, theme.fileInput.input]"
         @dragover.prevent="uploadDragoverEvent=true"
         @dragleave.prevent="uploadDragoverEvent=false"
         @drop.prevent="onUploadDropEvent"
         @click="openFileUpload"
    >
      <div v-if="loading" class="text-gray-600 dark:text-gray-400">
        <loader class="mx-auto h-6 w-6" />
        <p class="mt-2 text-center text-sm text-gray-500">Uploading your file...</p>
      </div>
      <template v-else>
        <div class="w-full text-center">
          <input
            ref="actual-input"
            class="hidden"
            :multiple="multiple"
            type="file"
            :name="name"
            :accept="acceptExtensions"
            @change="manualFileUpload"
          >
          <div v-if="file" key="file" class="flex flex-wrap items-center justify-center gap-4">
            <uploaded-file
              :file="file"
              :theme="theme"
              @remove="clearFile"
            />
          </div>
          <div v-else key="empty">
            <div v-if="currentUrl" class="flex flex-wrap items-center justify-center gap-4">
              <div class="relative w-full">
                <img class="w-full h-40 object-cover object-center rounded" :src="currentUrl" @error="isImageHide=true">
                <div class="absolute top-4 right-4">
                  <div class="hover:bg-gray-50 bg-white border border-gray-300 rounded-full p-2 transition-colors">
                    <a
                      href="javascript:void(0);"
                      class="flex items-center justify-center"
                      role="button"
                      title="Remove"
                      @click="clearUrl"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-5 text-gray-600">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-gray-400 w-full flex justify-center items-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
                <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z" />
              </svg>
            </div>
          </div>
        </div>
      </template>
    </div>

    <small v-if="help && helpPosition=='below_input'" :class="theme.default.help">
      <slot name="help"><span class="field-help" v-html="help" /></slot>
    </small>
    <has-error v-if="hasValidation" :form="form" :field="name" />
  </div>
</template>

<script>
import inputMixin from '~/mixins/forms/input.js'
import UploadedFile from './components/UploadedFile.vue'
import { resizeAndBase64EncodeImage } from '../../helpers/images'

export default {
  name: 'FileInput',

  components: { UploadedFile },
  mixins: [inputMixin],
  props: {
    multiple: { type: Boolean, default: true },
    mbLimit: { type: Number, default: 5 },
    accept: { type: String, default: '' },
    moveToFormAssets: { type: Boolean, default: false },
    helpPosition: {
      type: String,
      default: 'below_input',
      validator: value => ['above_input', 'below_input'].includes(value)
    }
  },

  data: () => ({
    file: null,
    uploadDragoverEvent: false,
    loading: false
  }),

  computed: {
    currentUrl () {
      return this.form[this.name]
    },
    acceptExtensions () {
      if (!this.accept) {
        return null
      }
      return this.accept
        .split(',')
        .map((i) => {
          return '.' + i.trim()
        })
        .join(',')
    }
  },

  watch: {
    file: {
      deep: true,
      handler (file) {
        if (file) {
          this.compVal = file.url
        } else {
          this.compVal = null
        }
      }
    }
  },

  async created () {
    if (typeof this.compVal === 'string' || this.compVal instanceof String) {
      await this.getFileFromUrl(this.compVal).then((fileObj) => {
        this.file = {
          file: fileObj,
          url: this.compVal,
          src: this.getFileSrc(fileObj)
        }
      })
    } else if (this.compVal && this.compVal.length > 0) {
      const tmpFiles = []
      for (let i = 0; i < this.compVal.length; i++) {
        await this.getFileFromUrl(this.compVal[i]).then((fileObj) => {
          tmpFiles.push({
            file: fileObj,
            url: this.compVal[i],
            src: this.getFileSrc(fileObj)
          })
        })
      }
      this.file = tmpFiles[0]
    }
  },

  methods: {
    clearAll () {
      this.file = null
    },
    clearFile () {
      this.file = null
    },
    onUploadDropEvent (e) {
      this.uploadDragoverEvent = false
      this.droppedFiles(e.dataTransfer.files)
    },
    droppedFiles (droppedFiles) {
      if (!droppedFiles || this.disabled) return

      for (let i = 0; i < droppedFiles.length; i++) {
        this.uploadFileToServer(droppedFiles.item(i))
      }
    },
    openFileUpload () {
      if (this.disabled) return
      this.$refs['actual-input'].click()
    },
    manualFileUpload (e) {
      const files = e.target.files
      for (let i = 0; i < files.length; i++) {
        this.uploadFileToServer(files.item(i))
      }
    },
    async uploadFileToServer (file) {
      if (this.disabled) return
      this.loading = true
      const resizedFile = await resizeAndBase64EncodeImage(file)
      this.storeFile(resizedFile)
        .then((response) => {
          this.file = {
            file: resizedFile,
            url: response.key,
            src: this.getFileSrc(resizedFile)
          }
          this.loading = false
        })
        .catch((error) => {
          this.clearAll()
          this.loading = false
        })
    },
    async getFileFromUrl (url, defaultType = 'image/jpeg') {
      const response = await fetch(url)
      const data = await response.blob()
      const name = url.replace(/^.*(\\|\/|\:)/, '')
      return new File([data], name, {
        type: data.type || defaultType
      })
    },
    getFileSrc (file) {
      if (file.type && file.type.split('/')[0] === 'image') {
        return URL.createObjectURL(file)
      }
      return null
    },
    clearUrl() {
      this.form[this.name] = null;
      this.$emit('input', null);
    }
  }
}
</script>
